import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import styles from './StateIndicator.module.css';

const StateIndicator = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const currentState = useSelector((state) => state.progression.currentState);
    const currentModule = useSelector((state) => state.progression.currentModule);

    // Références pour stocker les timeouts et éviter les conflits
    const hideTimeoutRef = useRef(null);
    const showTimeoutRef = useRef(null);
    const lastMouseMoveRef = useRef(Date.now()); // Timestamp du dernier mouvement de souris

    const messages = {
        OptometryLab: "Click to discover tools & product manipulation",
        TechnologyHub: "Click to discover our Unique Technology",
        Welcome: "Click on our services in the below menu to explore our virtual Clinic",
        ServiceCenter: "Click on the screen to discover our services",
        LdCenter: "Click to discover video and publications",
    };

    const markers = {
        OptometryLab: { icon: "🔍" },
        TechnologyHub: { icon: "💻" },
        Welcome: { icon: "👋" },
        ServiceCenter: { icon: "🖥️" },
        LdCenter: { icon: "📚" },
        IntroCamera: { icon: "" }
    };

    /** Gère la fermeture manuelle de l'indicateur */
    const handleClose = (e) => {
        e.stopPropagation();
        setIsDismissed(true);
        setIsVisible(false);

        // Réinitialiser après 30 secondes pour permettre à l'indicateur de réapparaître
        setTimeout(() => {
            setIsDismissed(false);
        }, 30000);
    };

    /** Gère la visibilité de l'indicateur après un mouvement de souris */
    const handleMouseMove = () => {
        // Si l'utilisateur a fermé manuellement l'indicateur, ne pas le réafficher
        if (isDismissed) return;

        lastMouseMoveRef.current = Date.now();

        // Nettoyage des timeouts existants
        clearTimeout(hideTimeoutRef.current);
        clearTimeout(showTimeoutRef.current);

        // Cache l'indicateur après 1 seconde de mouvement
        hideTimeoutRef.current = setTimeout(() => {
            setIsVisible(false);

            // Planifie la réapparition après 2 secondes d'inactivité
            showTimeoutRef.current = setTimeout(() => {
                if (!isDismissed) {
                    setIsVisible(true);
                }
            }, 2000);
        }, 800);
    };

    /** Effet qui surveille les changements de `currentState` */
    useEffect(() => {
        if (!messages[currentState]) {
            setIsVisible(false);
            return;
        }

        // Réinitialiser l'état de fermeture manuelle lors d'un changement d'état
        setIsDismissed(false);

        // Nettoyage des timeouts existants
        clearTimeout(hideTimeoutRef.current);
        clearTimeout(showTimeoutRef.current);

        // Affiche l'indicateur après 0.5 seconde lors d'un changement d'état
        const showDelay = setTimeout(() => {
            setIsVisible(true);
        }, 500);

        // Ajoute l'écouteur de mouvement de souris
        window.addEventListener('mousemove', handleMouseMove);

        return () => {
            clearTimeout(showDelay);
            clearTimeout(hideTimeoutRef.current);
            clearTimeout(showTimeoutRef.current);
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, [currentState]);

    // Si une modale est ouverte ou si l'indicateur n'est pas visible ou n'a pas de message, ne rien afficher
    const shouldDisplay = isVisible && !isDismissed && messages[currentState] && currentModule === "none";

    if (!shouldDisplay) return null;

    return (
        <div className={`${styles.container} ${currentModule !== "none" ? styles.hidden : ''}`}>
            {markers[currentState]?.icon && (
                <div className={styles.marker}>
                    {markers[currentState].icon}
                </div>
            )}
            <div className={styles.stateIndicator}>
                {messages[currentState]}
                <button
                    className={styles.closeButton}
                    onClick={handleClose}
                    aria-label="Fermer l'indicateur"
                ></button>
            </div>
        </div>
    );
};

export default StateIndicator;
